import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';
import { useSelector } from 'react-redux';
import config from '@plone/volto/registry';
import { useIsLoggedIn } from 'volto-ministerio-publico-federal/hooks/useIsLoggedIn';

export interface WorkflowStateInfo {
  state: string;
  label: string;
  color: string;
}

/**
 * Títulos de estado dos workflows padrão do Plone, que o `@workflow` devolve
 * em inglês quando a tradução do backend não alcança o estado. Chave: título
 * (ou id) em minúsculas. Título que não está aqui — como os do
 * `mpf_noticias_workflow`, já em português, ou estados novos — passa direto.
 */
const messages = defineMessages({
  private: { id: 'workflow_state_private', defaultMessage: 'Privado' },
  pending: {
    id: 'workflow_state_pending',
    defaultMessage: 'Aguardando revisão',
  },
  published: { id: 'workflow_state_published', defaultMessage: 'Publicado' },
  publicDraft: {
    id: 'workflow_state_public_draft',
    defaultMessage: 'Rascunho público',
  },
  internalDraft: {
    id: 'workflow_state_internal_draft',
    defaultMessage: 'Rascunho interno',
  },
  internallyPublished: {
    id: 'workflow_state_internally_published',
    defaultMessage: 'Publicado internamente',
  },
  externallyVisible: {
    id: 'workflow_state_externally_visible',
    defaultMessage: 'Visível externamente',
  },
});

const KNOWN_STATES: Record<string, MessageDescriptor> = {
  private: messages.private,
  pending: messages.pending,
  'pending review': messages.pending,
  published: messages.published,
  'public draft': messages.publicDraft,
  internal: messages.internalDraft,
  'internal draft': messages.internalDraft,
  internally_published: messages.internallyPublished,
  'internally published': messages.internallyPublished,
  external: messages.externallyVisible,
  'externally visible': messages.externallyVisible,
};

/** Cor neutra para estado de workflow sem cor em `workflowMapping`. */
const FALLBACK_COLOR = '#6b7280';

/** `internally_published` → `Internally published`; último recurso de rótulo. */
const humanize = (id: string) => {
  const text = id.replace(/[_-]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Cor e rótulo do estado de publicação, só para quem está logado e pode
 * editar o conteúdo ou mudar o estado — anônimo e leitor sem permissão continuam com a linha
 * neutra. Devolve `null` quando não há estado a mostrar.
 */
export const useWorkflowState = (
  reviewState?: string,
): WorkflowStateInfo | null => {
  const intl = useIntl();
  const isLoggedIn = useIsLoggedIn();
  // Quem edita ou quem pode mudar o estado (revisor de um item `pending` não
  // tem "edit", mas tem transições) enxerga a faixa; leitor comum, não.
  const canSeeState = useSelector(
    (state: any) =>
      (state.actions?.actions?.object ?? []).some(
        (action: { id: string }) => action.id === 'edit',
      ) || (state.workflow?.transitions ?? []).length > 0,
  );

  const current = useSelector(
    (state: any) =>
      state.workflow?.currentState as
        | { id?: string; title?: string }
        | undefined,
  );

  // Item novo (formulário de adição) não tem `review_state`, e
  // `workflow.currentState` ainda guarda o do pai: sem `reviewState`, nada a
  // mostrar. Com item existente, o `@workflow` (que a toolbar recarrega a cada
  // navegação e após cada transição) é a fonte mais atual do estado — o
  // `review_state` do conteúdo pode estar defasado depois de uma transição e
  // fica de fallback. O título vem do mesmo `@workflow`: é o do workflow real,
  // então estados novos aparecem sem mexer aqui; sem ele, o id humanizado
  // serve de rótulo.
  if (!reviewState || !isLoggedIn || !canSeeState) return null;
  const id = current?.id || reviewState;

  const serverTitle = current?.id === id ? current.title : undefined;
  const known = KNOWN_STATES[(serverTitle ?? id).toLowerCase()];
  const title = known ? intl.formatMessage(known) : serverTitle;

  const mapping = config.settings.workflowMapping as Record<
    string,
    { color?: string } | undefined
  >;

  return {
    state: id,
    label: title || humanize(id),
    color: mapping?.[id]?.color ?? FALLBACK_COLOR,
  };
};

export default useWorkflowState;
